import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Search, UploadCloud, ShoppingCart, LogOut, User, Users, Settings, HelpCircle } from "lucide-react";
import { resetUserTokenAction } from "../../redux/userSlice";
import { clearDistributorData } from "../../redux/searchSlice";
import { useAuth } from "AuthContext";
import Semi360Logo from "components/Semi360Logo";
import HelpModal from "./HelpModal";

const getInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "U";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { cart, adminLogout } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const { user, token } = useSelector((state) => state.user);

  const handleLogout = async () => {
    dispatch(resetUserTokenAction());
    adminLogout();
    navigate("/");
  };

  const handleSubmit = () => {
    dispatch(clearDistributorData());
    if (searchQuery) navigate(`/search?key=${searchQuery}`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get("key");
    setSearchQuery(key || "");
  }, [location]);

  useEffect(() => {
    if (!token) navigate("/");
  }, [navigate, token]);

  const initials = getInitials(user?.name);

  return (
    <header className="bg-s360-ink text-white flex items-center gap-4 px-4 h-12 shrink-0">
      {/* Brand */}
      <div
        className="flex items-center shrink-0 cursor-pointer select-none"
        onClick={() => navigate("/dashboard")}
      >
        <Semi360Logo className="h-[22px] w-auto text-white" />
      </div>

      {/* Global search */}
      <div className="flex-1 max-w-[540px] flex items-center gap-2 h-8 px-3 rounded bg-white/10 border border-white/10 focus-within:bg-white/[0.16] focus-within:border-s360-teal transition-colors">
        <Search className="w-3.5 h-3.5 text-white/70" />
        <input
          id="global-search"
          value={searchQuery || ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          placeholder="Search parts, MPNs, manufacturers…"
          className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder:text-white/55 min-w-0"
        />
        <span className="font-mono text-[11px] text-white/65 px-1.5 border border-white/20 rounded-sm">
          ⌘K
        </span>
      </div>

      {/* Upload BOM — direct upload here is retired (P10, ec5f74e); the Hub
          picker on /uploaded-bom is the only upload entry point now. */}
      <button
        type="button"
        onClick={() => navigate("/uploaded-bom")}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded bg-s360-teal text-s360-ink text-[13px] font-semibold cursor-pointer hover:bg-[#4FE0D0] transition-colors whitespace-nowrap"
      >
        <UploadCloud className="w-3.5 h-3.5" /> Upload BOM
      </button>

      {/* Right */}
      <div className="ml-auto flex items-center gap-2">
        {user?.company_name && (
          <span className="hidden md:inline text-xs font-medium text-s360-teal pr-3 border-r border-white/15">
            {user.company_name}
          </span>
        )}

        <button
          onClick={() => navigate("/cart")}
          title="Cart"
          className="relative w-8 h-8 inline-flex items-center justify-center rounded text-white/85 hover:bg-white/10 hover:text-white transition-colors"
        >
          <ShoppingCart className="w-5 h-5" />
          {cart.length > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[14px] h-3.5 px-1 inline-flex items-center justify-center rounded-full bg-s360-danger text-white text-[9px] font-bold leading-none">
              {cart.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setHelpOpen(true)}
          title="Report an issue"
          className="w-8 h-8 inline-flex items-center justify-center rounded text-white/85 hover:bg-white/10 hover:text-white transition-colors"
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              title={user?.name}
              className="w-7 h-7 rounded-full bg-s360-teal text-s360-ink inline-flex items-center justify-center text-[11px] font-bold hover:ring-2 hover:ring-s360-violet-100 transition-shadow"
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-60 p-1.5 rounded-lg border-s360-stroke1 shadow-lg"
          >
            <DropdownMenuLabel className="font-normal px-2 py-2">
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-full bg-s360-teal text-s360-ink inline-flex items-center justify-center text-[13px] font-bold shrink-0">
                  {initials}
                </span>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-s360-fg1 truncate">
                    {user?.name}
                  </span>
                  {user?.email && (
                    <span className="text-xs text-s360-fg3 truncate">{user.email}</span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-s360-stroke1" />
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-s360-fg1 focus:bg-s360-bg2 focus:text-s360-fg1"
              onClick={() => navigate("/orders")}
            >
              <ShoppingCart className="w-4 h-4 text-s360-fg3" /> My Orders
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-s360-fg1 focus:bg-s360-bg2 focus:text-s360-fg1"
              onClick={() => navigate("/profile")}
            >
              <User className="w-4 h-4 text-s360-fg3" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-s360-fg1 focus:bg-s360-bg2 focus:text-s360-fg1"
              onClick={() => navigate("/users")}
            >
              <Users className="w-4 h-4 text-s360-fg3" /> User Management
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-s360-fg1 focus:bg-s360-bg2 focus:text-s360-fg1"
              onClick={() => navigate("/settings")}
            >
              <Settings className="w-4 h-4 text-s360-fg3" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-s360-stroke1" />
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-sm font-semibold text-s360-danger focus:bg-s360-danger-bg focus:text-s360-danger"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} user={user} />
    </header>
  );
}
